import ParamsProvider from '@/components/providers/ParamProvider';
import { useParams } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useIsPathwayLocked } from '@/stores/pathwayStore';
import MonteCarloFields from './MonteCarloFields';
import { Fragment, useState, useEffect } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronDoubleUpIcon,
  ChevronDoubleDownIcon,
} from '@heroicons/react/24/outline';

import { patchNodeParams } from '@/api/nodes';
import { nodesActions, useAreAllParamsChecked, useAreAllParamsUnchecked, useCaseNodes } from '@/stores/nodesStore';
import PropTypes from 'prop-types';
import { caseActions } from '@/stores/caseStore';
import { analysisStatusActions } from '@/stores/analysisStatusStore';
import { panelActions, useShowTimeSeries } from '@/stores/panelStore';

const handleResponseData = data => {
  const { balanced, analysis_stale: analysisStale } = data;

  if (balanced) {
    caseActions.markBalanced();
  } else {
    caseActions.markUnbalanced();
  }

  if (analysisStale) {
    analysisStatusActions.clear();
  }
};

const updateNodeParams = async (params, urlParams) => {
  const { caseId, nodeId } = urlParams;
  const {
    params: { [nodeId]: nodeParams },
  } = params;
  const { data } = await patchNodeParams(caseId, nodeId, nodeParams);

  const { params: updatedParams } = data;
  nodesActions.updateParams(nodeId, updatedParams);
  handleResponseData(data);
};

const MonteCarloParams = ({ data }) => {
  const { nodes } = data;
  const { caseId } = useParams();
  const [openNodeIds, setOpenNodeIds] = useState(nodes.length > 0 ? [nodes[0].id] : []);
  const [allExpanded, setAllExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const caseNodes = useCaseNodes();
  const methods = useForm({ mode: 'onChange' });
  const locked = useIsPathwayLocked();
  const { handleSubmit, reset } = methods;
  const allChecked = useAreAllParamsChecked();
  const allUnchecked = useAreAllParamsUnchecked();
  const showTimeSeries = useShowTimeSeries();

  const onInnerSubmit = async data => {
    reset(data);
  };

  const checkKeyDown = e => {
    if (e.key === 'Enter') {
      if (document.activeElement.tagName !== 'body') {
        document.activeElement.blur();
      }
    }
  };

  const toggleNode = nodeId => {
    setOpenNodeIds(prevState =>
      prevState.includes(nodeId) ? prevState.filter(id => id !== nodeId) : [...prevState, nodeId],
    );
  };

  const toggleAllNodes = () => {
    if (allExpanded) {
      setOpenNodeIds([]);
    } else {
      setOpenNodeIds(nodes.map(node => node.id));
    }
    setAllExpanded(!allExpanded);
  };

  const checkAll = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const data = await nodesActions.checkAllParams(caseId);
      handleResponseData(data);
    } finally {
      setIsLoading(false);
    }
  };

  const uncheckAll = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const data = await nodesActions.uncheckAllParams(caseId);
      handleResponseData(data);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setAllExpanded(openNodeIds.length === nodes.length);
  }, [openNodeIds, nodes.length]);

  return (
    <ParamsProvider readOnly={locked} patch={updateNodeParams}>
      <FormProvider {...methods}>
        <form
          onKeyDown={checkKeyDown}
          onSubmit={handleSubmit(onInnerSubmit)}
          noValidate
          className="h-full flex flex-col"
        >
          <div className="flex justify-between mb-4 mt-4">
            <div>
              <button
                onClick={checkAll}
                className="btn btn-sm bg-blue-500 text-white mr-2"
                disabled={allChecked || isLoading}
              >
                Check All
              </button>
              <button
                onClick={uncheckAll}
                className="btn btn-sm bg-blue-500 text-white"
                disabled={allUnchecked || isLoading}
              >
                Uncheck All
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => panelActions.toggleTimeSeries()} className="btn btn-sm flex items-center">
                {showTimeSeries ? 'Disable' : 'Enable'} Time Varying Parameters
              </button>
              {allExpanded ? (
                <button onClick={toggleAllNodes} className="btn btn-sm flex items-center">
                  <ChevronDoubleUpIcon className="h-5 w-5 mr-2" />
                  Collapse All
                </button>
              ) : (
                <button onClick={toggleAllNodes} className="btn btn-sm flex items-center">
                  <ChevronDoubleDownIcon className="h-5 w-5 mr-2" />
                  Expand All
                </button>
              )}
            </div>
          </div>
          <table className="w-full table-fixed">
            <thead className="font text-xs">
              <tr className="border-b-2 border-ivory-100">
                <th className="py-2 w-2/5">Parameter</th>
                <th className="py-2 w-1/5">Minimum</th>
                <th className="py-2 w-1/5">Baseline</th>
                <th className="py-2 w-1/5">Maximum</th>
                <th className="py-2 w-1/5">Unit</th>
                {showTimeSeries && (
                  <>
                    <th className="py-2 w-1/5">Annual Change (%)</th>
                    <th className="py-2 w-1/5">Beginning</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {nodes.map(node => (
                <Fragment key={node.id}>
                  <tr onClick={() => toggleNode(node.id)} className="cursor-pointer border-b-2 border-ivory-100">
                    <td colSpan={5} className="text-md font-bold py-4 flex items-center">
                      {openNodeIds.includes(node.id) ? (
                        <ChevronDownIcon className="h-6 w-6 mr-4 text-info rounded-md shadow-md" />
                      ) : (
                        <ChevronRightIcon className="h-6 w-6 mr-4 text-blue-500 rounded-md shadow-md" />
                      )}
                      {node.label}
                    </td>
                  </tr>
                  {openNodeIds.includes(node.id) && (
                    <MonteCarloFields nodeId={node.id} caseParams={caseNodes?.[node.id]?.params} params={node.params} />
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </form>
      </FormProvider>
    </ParamsProvider>
  );
};

MonteCarloParams.propTypes = {
  data: PropTypes.object.isRequired,
};

export default MonteCarloParams;
